import { Component, inject, OnInit, OnDestroy, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ScaffoldComponent } from 'src/app/shared/scaffold/scaffold.component';
import { Site } from 'src/app/interfaces/site';
import {
  IonLabel,
  IonText,
  IonButton,
  IonIcon,
  IonSpinner,
} from '@ionic/angular/standalone';
import { location, star, chevronDown } from 'ionicons/icons';
import { addIcons } from 'ionicons';
import { SitesService } from 'src/app/services/sites.service';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { Type } from 'src/app/interfaces/enums/site/type';
import { MarkdownModule } from 'ngx-markdown';
import {
  ratingToStars,
  starsToRating,
} from 'src/app/interfaces/enums/booking/rating';
import { BookingService } from 'src/app/services/booking.service';

@Component({
  selector: 'app-site',
  template: `
    <app-scaffold [title]="'Sitio recomendado'" [backRouter]="backRouter()">
      <section class="flex flex-col gap-4 text-base md:text-xl mb-28">
        @if (isLoading()) {
          <div class="flex justify-center items-center">
            <ion-spinner name="dots" class="text-flow-white"></ion-spinner>
          </div>
        }

        @if (!isLoading() && site()) {
          <img
            [src]="site()!.imagen"
            class="w-full h-52 lg:h-72 object-cover rounded-3xl border-2 border-flow-white"
            [alt]="site()!.nombre"
          />
          <ion-label class="text-flow-yellow mt-4 lg:mt-2">
            {{ getTypeLabel(site()!.tipo) }}
          </ion-label>
          <ion-label class="font-bold text-lg md:text-2xl lg:text-3xl">{{
            site()!.nombre
          }}</ion-label>
          <div class="flex items-center gap-2">
            <ion-icon name="location" class="text-flow-yellow"></ion-icon>
            <ion-text>{{ site()!.direccion }}</ion-text>
          </div>
          <div [class.line-clamp]="!showFullDescription()">
            <markdown [data]="site()!.descripcion"></markdown>
          </div>
          <ion-text
            class="flex items-center gap-2 cursor-pointer decoration-1 underline decoration-gray-300
              hover:text-flow-yellow hover:decoration-flow-yellow transition-all duration-150"
            (click)="toggleDescription()"
          >
            {{ showFullDescription() ? 'Mostrar menos' : 'Leer más' }}
            <ion-icon name="chevron-down"></ion-icon>
          </ion-text>
          <ion-button
            class="bg-flow-yellow text-flow-green rounded-full font-bold text-base md:text-xl mt-6 lg:mt-2 py-2 lg:py-1"
            fill="clear"
            shape="round"
            size="small"
            [href]="site()!.url"
            target="_blank"
          >
            VER EN GOOGLE MAPS
          </ion-button>

          <ion-label class="font-bold mt-8 lg:mt-2">
            Valora este sitio
          </ion-label>
          <div
            class="grid grid-cols-3 gap-4 md:gap-10 lg:gap-20 items-center justify-between mr-1"
          >
            @for (rate of [3, 2, 1]; track rate) {
              <ion-button
                fill="clear"
                shape="round"
                class="bg-flow-yellow text-flow-green rounded-full cursor-pointer w-full"
                [class.selected]="selectedRate() === rate"
                (click)="rateSite(rate)"
              >
                <img
                  [src]="'assets/images/estrella ' + rate + '.svg'"
                  class="w-full max-w-[30px] md:max-w-[40px] lg:my-1"
                  [alt]="
                    'Pulsando en esta opción, se valorará con ' +
                    rate +
                    ' estrellas'
                  "
                />
              </ion-button>
            }
          </div>
        }
      </section>
    </app-scaffold>
  `,
  styles: [
    `
      .selected {
        border-width: 4px;
        border-color: var(--flow-white);
      }

      .line-clamp {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        -webkit-line-clamp: 3;
        line-height: 1.5em;
        max-height: 4.5em;
      }
    `,
  ],
  standalone: true,
  imports: [
    ScaffoldComponent,
    IonLabel,
    IonText,
    MarkdownModule,
    IonButton,
    IonIcon,
    IonSpinner,
  ],
})
export default class SitePage implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly sitesService = inject(SitesService);
  private readonly bookingService = inject(BookingService);
  private readonly destroy$ = new Subject<void>();

  protected readonly siteName = this.route.snapshot.params['site'];
  protected backRouter = signal<string>('');
  protected site = signal<Site | null>(null);
  protected isLoading = signal<boolean>(true);
  protected selectedRate = signal<number>(0);
  protected showFullDescription = signal<boolean>(false);

  constructor() {
    addIcons({ location, star, chevronDown });
  }

  ngOnInit(): void {
    this.route.data.pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.backRouter.set(
          `/destinations/${data['nombre']}/${data['destino']}/sites`,
        );
        const bookingId = data['nombre'];

        if (!this.siteName || !bookingId) {
          this.isLoading.set(false);
          return;
        }

        this.loadSiteAndBooking(bookingId);
      },
      error: (error) => {
        console.error('Error loading route data:', error);
        this.isLoading.set(false);
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected getTypeLabel(type: string): string {
    return Type[type as keyof typeof Type];
  }

  protected toggleDescription(): void {
    this.showFullDescription.update((value) => !value);
  }

  protected async rateSite(rate: number): Promise<void> {
    try {
      this.selectedRate.set(rate);
      const bookingId = this.route.snapshot.data['nombre'];

      if (!this.site() || !bookingId) {
        throw new Error('Missing required data for rating');
      }

      const ratingValue = starsToRating(rate);
      await this.bookingService.updateRecommendedSiteRating(
        bookingId,
        this.site()!.id!,
        ratingValue,
      );
    } catch (error) {
      console.error('Error rating site:', error);
      this.selectedRate.set(0);
    }
  }

  private loadSiteAndBooking(bookingId: string): void {
    const site$ = this.sitesService.getSiteById(this.siteName);
    const booking$ = this.bookingService.getBookingById(bookingId);

    combineLatest([site$, booking$])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ([site, booking]) => {
          this.site.set(site);
          this.isLoading.set(false);

          if (booking?.sitiosRecomendados && site) {
            const recommendedSite = booking.sitiosRecomendados.find(
              (r) => r.sitio.id === site.id,
            );
            if (recommendedSite?.valoracion) {
              this.selectedRate.set(ratingToStars(recommendedSite.valoracion));
            }
          }
        },
        error: (error) => {
          console.error('Error loading site and booking:', error);
          this.isLoading.set(false);
        },
      });
  }
}
